// CaseFilePage — /households/:caseId — the 90% workspace (design brief).
//
// One household, everything about it: identity first (who and where), state
// second (source-tagged facts + thread docket), attention third (signals),
// actions last. Blocked actions are statements — the precondition is shown,
// never a hidden lock.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { toast } from '@/components/Toast';
import {
  fetchCaseFile,
  formatAge,
  formatWhen,
  requestCaseIntake,
  type CaseFilePayload,
  type CaseIntake,
  type CaseIntakeSection,
  type ThreadRow,
  type ActivityEvent,
} from '@/lib/commandCenterClient';
import { resendRequests } from '@/lib/requestsClient';
import { resendDoc, type DocListRow } from '@/lib/viewDocsClient';
import type { HouseholdDocket } from '@/lib/commandCenterClient';
import {
  FactListView,
  OcEmpty,
  OcError,
  OcLoading,
  SignalRowView,
  StatusChip,
  TwoTrack,
} from '@/components/oc/bits';
import { ConsequenceConfirm } from '@/components/oc/ConsequenceConfirm';

// ---------------------------------------------------------------------------
// Docket row — one thread: type · two-track state · age. Links to the thread.
// ---------------------------------------------------------------------------

function DocketRow({ thread }: { thread: ThreadRow }) {
  return (
    <Link
      className="oc-docket-row"
      to={`/threads/${encodeURIComponent(thread.id)}`}
    >
      <span style={{ fontWeight: 'var(--weight-medium)' }}>{thread.typeLabel}</span>
      <TwoTrack
        phase={thread.phase}
        nativeStateLabel={thread.nativeStateLabel}
        signature={thread.signature}
      />
      <span className="oc-meta" style={{ marginLeft: 'auto', flexShrink: 0 }}>
        {formatAge(thread.ageDays)}
        {thread.neverSent ? ' · never sent' : ''}
      </span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Documents — every document this household has ever been sent (WEL-144).
// ---------------------------------------------------------------------------
//
//   Miles: "The Household list should contain full history of everything our
//   system has touched for the whole household."
//
// And, the reason the resend button is HERE rather than only on the Requests
// dashboard: an operator who has just found the household is standing in front
// of the thing they came to act on. Sending them somewhere else to act on it is
// where the trail goes cold — it is the same failure as a Past list you cannot
// search, one step later.
//
// The rows are the SAME shape /api/docs returns, so this list and the Docs page
// can never disagree about what exists or what state it is in. `resendable` is
// the server's answer, not a guess: a drawn button always does something.

/** State → chip tone. Finished states read closed; live ones read active. */
const DOC_STATE_TONE: Record<string, 'active' | 'active-soft' | 'done' | 'closed' | 'blocked'> = {
  draft: 'closed',
  sent: 'active',
  in_progress: 'active-soft',
  completed: 'done',
  declined: 'blocked',
  cancelled: 'closed',
  voided: 'closed',
  superseded: 'closed',
};

function DocumentsPanel({ docket }: { docket: HouseholdDocket }) {
  const [confirm, setConfirm] = useState<DocListRow | null>(null);
  const [busy, setBusy] = useState(false);

  const runResend = async () => {
    const row = confirm;
    if (!row) return;
    setBusy(true);
    try {
      const res = await resendDoc(row.kind, row.id);
      if (!res.ok) {
        const reason = res.results?.find((r) => !r.ok)?.reason;
        throw new Error(reason ? `Couldn't send it again: ${reason}` : "Couldn't send it again.");
      }
      toast.success(
        `${row.label} re-sent to ${res.sent} recipient${res.sent === 1 ? '' : 's'} — the reminder clock restarted.`,
      );
      setConfirm(null);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't send it again"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="oc-panel">
      <div className="oc-kicker">Documents — everything we have sent</div>
      {docket.rows.length === 0 ? (
        <OcEmpty>Nothing has been sent to this household yet.</OcEmpty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {docket.rows.map((row) => {
            // A withheld row carries its TYPE and nothing else — no people, no
            // property, no state, not clickable. It exists so the list is honest
            // about being scoped rather than silently short.
            if (row.withheld) {
              return (
                <div
                  key={`${row.kind}:${row.id}`}
                  className="oc-docket-row"
                  style={{ color: 'var(--fg-muted)', cursor: 'default' }}
                >
                  <span style={{ fontWeight: 'var(--weight-medium)' }}>{row.label}</span>
                  <span className="oc-meta">
                    {row.withheldReason ?? 'restricted to the accommodation roster'}
                  </span>
                </div>
              );
            }
            return (
              <div
                key={`${row.kind}:${row.id}`}
                className="oc-docket-row"
                style={{ alignItems: 'center', gap: 'var(--space-3)' }}
              >
                <Link
                  to={`/docs?sk=${encodeURIComponent(row.kind)}&sid=${encodeURIComponent(row.id)}`}
                  style={{ flex: '1 1 auto', minWidth: 0, color: 'inherit', textDecoration: 'none' }}
                >
                  <span style={{ fontWeight: 'var(--weight-medium)' }}>{row.label}</span>
                  {row.digest && (
                    <span className="oc-cell-sub" style={{ display: 'block' }}>
                      {row.digest}
                    </span>
                  )}
                </Link>
                <StatusChip tone={DOC_STATE_TONE[row.state] ?? 'closed'}>
                  {row.stateLabel ?? row.state}
                </StatusChip>
                <span className="oc-meta" style={{ flexShrink: 0 }}>
                  {formatWhen(row.updatedAt ?? null)}
                </span>
                {/* Act on it from where you found it. */}
                {row.resendable && (
                  <button
                    type="button"
                    className="btn"
                    data-variant="secondary"
                    data-size="sm"
                    style={{ flexShrink: 0 }}
                    onClick={() => setConfirm(row)}
                  >
                    Send again
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Scope, stated once, muted — a standing statement rather than an alert. */}
      {docket.withheld && docket.withheld.length > 0 && (
        <div className="oc-meta" style={{ marginTop: 'var(--space-3)' }}>
          Accommodation records are not shown here — they are restricted to the
          accommodation roster.
        </div>
      )}
      {docket.gaps && docket.gaps.length > 0 && (
        <div className="oc-meta" style={{ marginTop: 'var(--space-2)' }}>
          {docket.gaps.reduce((n, g) => n + g.count, 0)} document
          {docket.gaps.reduce((n, g) => n + g.count, 0) === 1 ? '' : 's'} of a type this
          viewer does not yet know how to show ({docket.gaps.map((g) => g.kind).join(', ')}).
        </div>
      )}

      <ConsequenceConfirm
        open={confirm !== null}
        title={`Send the ${confirm?.label.toLowerCase() ?? 'document'} again?`}
        consequence="This emails everyone still outstanding on it their existing link again and restarts the reminder clock. Nothing is regenerated — it is the same document and the same link."
        confirmLabel="Send again"
        tone="default"
        busy={busy}
        onConfirm={() => void runResend()}
        onCancel={() => {
          if (!busy) setConfirm(null);
        }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Intake panel — pre-lease intake state + in-place actions (C follow-up).
// Request an intake / re-send a section's link without leaving the case view:
// both reuse the EXISTING machinery (the targeted intake-watch sweep and the
// Requests dashboard resend endpoint — same emails, same guards).
// ---------------------------------------------------------------------------

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.message ?? body?.error ?? `${fallback} (HTTP ${err.status}).`;
  }
  return err instanceof Error ? err.message : fallback;
}

function CaseIntakePanel({
  caseId,
  intake,
  onChanged,
}: {
  caseId: string;
  intake: CaseIntake;
  /** Refresh the case snapshot after a successful action. */
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<null | 'request' | 'resend'>(null);
  const [confirmRequest, setConfirmRequest] = useState(false);
  const [confirmResend, setConfirmResend] = useState<CaseIntakeSection | null>(null);

  const runRequest = async () => {
    setBusy('request');
    try {
      const res = await requestCaseIntake(caseId);
      setConfirmRequest(false);
      if (res.requested > 0) {
        toast.success(
          `Requested ${res.requested} intake section${res.requested === 1 ? '' : 's'} — the applicant has been emailed the intake link.`,
        );
      } else {
        toast.info(
          'No intake sections became requestable — they may already be requested or completed, or their trigger point has not arrived yet.',
        );
      }
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't request the intake"));
    } finally {
      setBusy(null);
    }
  };

  const runResend = async () => {
    const section = confirmResend;
    if (!section?.resendId) return;
    setBusy('resend');
    try {
      const res = await resendRequests([section.resendId]);
      if (res.failed > 0) {
        const reason = res.results[0]?.reason;
        throw new Error(reason ? `Couldn't re-send the link: ${reason}` : "Couldn't re-send the link.");
      }
      toast.success(`${section.label} intake link re-sent — the reminder clock restarted.`);
      setConfirmResend(null);
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't re-send the intake link"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="oc-kicker" style={{ marginTop: 'var(--space-4)' }}>
        Intake
      </div>
      {intake.sections.length === 0 ? (
        <OcEmpty>No intake yet for this household.</OcEmpty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {intake.sections.map((s) => (
            <div
              key={s.section}
              style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}
            >
              <span style={{ fontWeight: 'var(--weight-medium)' }}>{s.label}</span>
              <span className="oc-meta">
                {s.statusLabel}
                {s.status === 'requested' && s.requestedAt
                  ? ` · requested ${formatWhen(s.requestedAt)}`
                  : ''}
                {s.status === 'completed' && s.completedAt
                  ? ` · ${formatWhen(s.completedAt)}`
                  : ''}
              </span>
              {s.resendId && (
                <button
                  type="button"
                  className="btn"
                  data-variant="secondary"
                  data-size="sm"
                  disabled={busy !== null}
                  onClick={() => setConfirmResend(s)}
                >
                  Re-send link
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {intake.canRequest ? (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            disabled={busy !== null}
            onClick={() => setConfirmRequest(true)}
          >
            {busy === 'request' ? 'Requesting…' : 'Request intake'}
          </button>
        </div>
      ) : (
        intake.requestNote && (
          <div className="oc-meta" style={{ marginTop: 'var(--space-2)' }}>
            {intake.requestNote}
          </div>
        )
      )}

      <ConsequenceConfirm
        open={confirmRequest}
        title="Request the pre-lease intake?"
        consequence="This runs the intake request for this household now — every section that is due (resident designation, yard care, animals) is requested and the applicant is emailed the standard intake link. Sections already requested or completed are untouched."
        confirmLabel="Request intake"
        tone="default"
        busy={busy === 'request'}
        onConfirm={() => void runRequest()}
        onCancel={() => {
          if (busy !== 'request') setConfirmRequest(false);
        }}
      />
      <ConsequenceConfirm
        open={confirmResend !== null}
        title={`Re-send the ${confirmResend?.label.toLowerCase() ?? 'intake'} link?`}
        consequence="This emails the responsible applicant their existing intake link again and restarts the reminder clock. Nothing is re-requested — it is the same form and the same link."
        confirmLabel="Re-send link"
        tone="default"
        busy={busy === 'resend'}
        onConfirm={() => void runResend()}
        onCancel={() => {
          if (busy !== 'resend') setConfirmResend(null);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Activity feed — timestamp + statement rows.
// ---------------------------------------------------------------------------

function ActivityFeed({ activity }: { activity: ActivityEvent[] }) {
  if (activity.length === 0) {
    return <OcEmpty>No recorded activity.</OcEmpty>;
  }
  return (
    <div>
      {activity.map((ev, i) => (
        <div key={i} className="oc-feed-item">
          <span className="oc-feed-time">{formatWhen(ev.at)}</span>
          <span>{ev.text}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CaseFilePage() {
  const { caseId } = useParams();

  const [data, setData] = useState<CaseFilePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const liveRef = useRef(0);

  const load = useCallback(async () => {
    if (!caseId) return;
    const token = ++liveRef.current;
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchCaseFile(caseId);
      if (liveRef.current !== token) return;
      setData(payload);
    } catch (err) {
      if (liveRef.current !== token) return;
      setError(
        err instanceof ApiError
          ? `Couldn't load the case file (${err.status}).`
          : err instanceof Error
            ? err.message
            : "Couldn't load the case file.",
      );
    } finally {
      if (liveRef.current === token) setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <OcLoading label="Loading case file…" />;
  if (error) {
    return (
      <OcError>
        <div>{error}</div>
        <div style={{ marginTop: 'var(--space-3)' }}>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      </OcError>
    );
  }
  if (!data) return null;

  const adults = data.people.filter((p) => !p.isMinor).length;
  const minors = data.people.length - adults;
  const whereBits = [data.propertyName, data.unitName, data.afStatusLabel].filter(
    (v): v is string => !!v,
  );

  const active = data.threads.filter(
    (t) => t.phase !== 'done' && t.phase !== 'closed',
  );
  const completed = data.threads.filter(
    (t) => t.phase === 'done' || t.phase === 'closed',
  );

  // Durable case (Phase 3): this group's whole case — every member group across
  // renewals + every case thread. The docket above already shows THIS group's
  // threads, so the case panel lists only the threads living on OTHER member
  // groups (docket ∪ panel = all case threads).
  const durableCase = data.durableCase ?? null;
  const docketIds = new Set(data.threads.map((t) => t.id));
  const otherCaseThreads = durableCase
    ? durableCase.threads.filter((t) => !docketIds.has(t.id))
    : [];

  // Lease builder, right in the header (Miles, 2026-08-28): a CONVERTED tenancy
  // no longer sits on the Applications list by default, so the household file
  // is where staff land when they need to send a corrected lease — and the way
  // into the builder has to be obvious from here, not three panels down. The
  // href is the server's own `builder` action (household cases only; owner and
  // tenant-only files carry no application group and therefore no such action),
  // so this link and the Actions panel can never disagree about where it goes.
  const builderAction =
    data.kind === 'household'
      ? data.actions.find((a) => a.id === 'builder' && a.enabled && !!a.href) ?? null
      : null;

  return (
    <div className="oc-page">
      {/* ---------- Header: who and where ---------- */}
      <header className="oc-panel">
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 'var(--space-4)',
            flexWrap: 'wrap',
          }}
        >
          <h1 className="oc-page-title">{data.name}</h1>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: 'var(--space-3)',
              flexWrap: 'wrap',
            }}
          >
            {builderAction?.href && (
              <Link
                className="btn"
                data-variant="secondary"
                data-size="sm"
                to={builderAction.href}
                title={
                  data.converted
                    ? 'Open the lease builder for this household — the way to send a corrected lease once AppFolio has converted the applicants to tenants.'
                    : 'Open the lease builder for this household.'
                }
              >
                Lease builder
              </Link>
            )}
            <span className="oc-meta">
              AppFolio · as of {formatWhen(data.freshAsOf)}
            </span>
          </span>
        </div>

        <div
          className="oc-meta"
          style={{
            marginTop: 'var(--space-2)',
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          {whereBits.map((bit, i) => (
            <span key={i}>
              {i > 0 && <span aria-hidden="true">· </span>}
              {bit}
            </span>
          ))}
          {data.converted && <StatusChip tone="closed">Converted</StatusChip>}
          <span>
            {whereBits.length > 0 && <span aria-hidden="true">· </span>}
            {adults} adults, {minors} minors
          </span>
        </div>

        {data.people.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginTop: 'var(--space-3)',
            }}
          >
            {data.people.map((p, i) => (
              <span
                key={i}
                className="oc-token"
                {...(p.isMinor ? { style: { color: 'var(--fg-muted)' } } : {})}
              >
                {p.name} — {p.isLead ? 'lead · ' : ''}
                {p.type}
                {p.isMinor
                  ? ` · minor${p.comingOfAge ? `, 18 on ${p.comingOfAge}` : ''}`
                  : ''}
              </span>
            ))}
          </div>
        )}
      </header>

      {/* ---------- Durable case — the household's file across lease cycles ---------- */}
      {durableCase && (
        <section className="oc-panel">
          <div className="oc-kicker">Case — across lease cycles</div>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontWeight: 'var(--weight-medium)' }}>
              {durableCase.householdName ?? 'This case'}
            </span>
            <span className="oc-meta">
              started {formatWhen(durableCase.startedAt)}
              {durableCase.lastLeaseAt
                ? ` · last lease sent ${formatWhen(durableCase.lastLeaseAt)}`
                : ''}
              {' · '}
              {durableCase.members.length === 1
                ? '1 lease cycle'
                : `${durableCase.members.length} lease cycles`}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginTop: 'var(--space-2)',
            }}
          >
            {durableCase.members.map((m) => {
              const label = `${m.linkedVia === 'renewal' ? 'Renewal' : 'Original'}${
                m.isCurrent ? ' · current' : ''
              }`;
              return m.isThisGroup ? (
                <span key={m.groupId} className="oc-token">
                  {label} — this file
                </span>
              ) : (
                <Link
                  key={m.groupId}
                  className="oc-token"
                  to={`/households/${encodeURIComponent(m.groupId)}`}
                  title="Open this lease cycle's case file"
                >
                  {label}
                  {m.linkedAt ? ` · ${formatWhen(m.linkedAt)}` : ''}
                </Link>
              );
            })}
          </div>
          {otherCaseThreads.length > 0 ? (
            <>
              <div className="oc-kicker" style={{ marginTop: 'var(--space-3)' }}>
                Threads elsewhere in this case
              </div>
              {otherCaseThreads.map((t) => (
                <DocketRow key={t.id} thread={t} />
              ))}
            </>
          ) : (
            <div className="oc-meta" style={{ marginTop: 'var(--space-2)' }}>
              {durableCase.members.length > 1
                ? 'Every thread in this case lives on this file.'
                : 'This is the only lease cycle in the case so far.'}
            </div>
          )}
        </section>
      )}

      {/* ---------- Body: facts · docket · actions & signals ---------- */}
      <div className="oc-grid-3">
        <section className="oc-panel">
          <div className="oc-kicker">Snapshot — source-tagged facts</div>
          {data.facts.length === 0 ? (
            <OcEmpty>No facts on file.</OcEmpty>
          ) : (
            <FactListView facts={data.facts} />
          )}
        </section>

        <section className="oc-panel">
          <div className="oc-kicker">Threads — active first</div>
          {data.threads.length === 0 ? (
            <OcEmpty>No agreements yet for this household.</OcEmpty>
          ) : (
            <>
              {active.map((t) => (
                <DocketRow key={t.id} thread={t} />
              ))}
              {completed.length > 0 && (
                <details style={{ marginTop: 'var(--space-2)' }}>
                  <summary className="oc-meta" style={{ cursor: 'pointer' }}>
                    Completed ({completed.length})
                  </summary>
                  {completed.map((t) => (
                    <DocketRow key={t.id} thread={t} />
                  ))}
                </details>
              )}
            </>
          )}
        </section>

        <section className="oc-panel">
          <div className="oc-kicker">Actions</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.actions.length === 0 && <OcEmpty>No actions available.</OcEmpty>}
            {data.actions.map((a) =>
              a.enabled && a.href ? (
                <Link
                  key={a.id}
                  className="btn"
                  data-variant="secondary"
                  data-size="sm"
                  to={a.href}
                >
                  {a.label}
                </Link>
              ) : (
                <div key={a.id}>
                  <button
                    type="button"
                    className="btn"
                    data-variant="secondary"
                    data-size="sm"
                    disabled
                  >
                    {a.label}
                  </button>
                  {a.blockedReason && (
                    <div className="oc-meta" style={{ marginTop: 2 }}>
                      {a.blockedReason}
                    </div>
                  )}
                </div>
              ),
            )}
          </div>

          {data.intake && caseId && (
            <CaseIntakePanel
              caseId={caseId}
              intake={data.intake}
              onChanged={() => void load()}
            />
          )}

          <div className="oc-kicker" style={{ marginTop: 'var(--space-4)' }}>
            Signals
          </div>
          {data.signals.length === 0 ? (
            <OcEmpty>No open signals.</OcEmpty>
          ) : (
            data.signals.map((s) => (
              <SignalRowView key={s.id} signal={s} onChanged={() => void load()} />
            ))
          )}
        </section>
      </div>

      {/* ---------- Documents ---------- */}
      {data.docket && <DocumentsPanel docket={data.docket} />}

      {/* ---------- Activity ---------- */}
      <section className="oc-panel">
        <div className="oc-kicker">Activity</div>
        <ActivityFeed activity={data.activity} />
      </section>
    </div>
  );
}
